/**
 * TaskScheduler — sdílená komponenta pro plánování úloh přes Tasks modul.
 *
 * Použití:
 *   <TaskScheduler toolFilter="backup" defaultTool="backup_module" defaultModule="toolbox" />
 *   <TaskScheduler toolFilter="health" defaultTool="health_monitor" defaultModule="toolbox" />
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import { Button } from './Button';
import { Badge } from './Badge';
import { Select } from './Select';
import { Input } from './Input';
import { Toggle } from './Toggle';
import { Modal } from './Modal';
import { ConfirmDialog } from './ConfirmDialog';
import { toast } from 'sonner';
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  Plus,
  Trash2,
  X,
  Edit2,
  History,
} from 'lucide-react';

// ── Types ───────────────────────────────────────────────────────────────────

interface ScheduledTask {
  id: string;
  name: string;
  module: string;
  tool: string;
  args: Record<string, unknown> | null;
  trigger_type: string;
  trigger_config: Record<string, unknown>;
  enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  next_run_at: string | null;
  description?: string | null;
}

interface TaskRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  triggered_by: string;
  event_name: string | null;
  output: string | null;
  error: string | null;
}

interface TaskSchedulerProps {
  /** Filter tasks by tool name (substring match) */
  toolFilter: string;
  /** Default tool for new tasks */
  defaultTool: string;
  /** Default module owning the tool */
  defaultModule: string;
  /** Optional: fixed args for new tasks */
  defaultArgs?: Record<string, unknown>;
  /** Optional: hide create form */
  readonly?: boolean;
}

// ── Presets ──────────────────────────────────────────────────────────────────

const FREQUENCY_PRESETS = [
  { value: 'every5m', label: 'Každých 5 minut', cron: '*/5 * * * *' },
  { value: 'every15m', label: 'Každých 15 minut', cron: '*/15 * * * *' },
  { value: 'every30m', label: 'Každých 30 minut', cron: '*/30 * * * *' },
  { value: 'hourly', label: 'Každou hodinu', cron: '0 * * * *' },
  { value: 'daily3am', label: 'Denně v 3:00', cron: '0 3 * * *' },
  { value: 'daily6am', label: 'Denně v 6:00', cron: '0 6 * * *' },
  { value: 'weekly', label: 'Týdně (neděle 3:00)', cron: '0 3 * * 0' },
  { value: 'monthly', label: 'Měsíčně (1. v 3:00)', cron: '0 3 1 * *' },
  { value: 'custom', label: 'Vlastní cron...', cron: '' },
];

const EVENT_PRESETS = [
  'backup.complete',
  'backup.error',
  'deploy.done',
  'deploy.error',
  'container.started',
  'container.stopped',
  'module.restarted',
];

// ── Component ───────────────────────────────────────────────────────────────

export function TaskScheduler(
  { toolFilter, defaultTool, defaultModule, defaultArgs, readonly }: TaskSchedulerProps,
) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editingTask, setEditingTask] = useState<ScheduledTask | null>(null);
  const [viewingTask, setViewingTask] = useState<ScheduledTask | null>(null);
  const [deleteTask, setDeleteTask] = useState<ScheduledTask | null>(null);
  const [triggerType, setTriggerType] = useState<'cron' | 'interval' | 'event'>('cron');
  const [freqPreset, setFreqPreset] = useState('daily3am');
  const [customCron, setCustomCron] = useState('');
  const [intervalSec, setIntervalSec] = useState(300);
  const [eventName, setEventName] = useState('');
  const [taskName, setTaskName] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [filterEnabled, setFilterEnabled] = useState<'all' | 'on' | 'off'>('all');
  // backup_module needs module name in args
  const [argModule, setArgModule] = useState(
    () => String((defaultArgs as { module?: string } | undefined)?.module ?? ''),
  );

  const modulesQ = useQuery({
    queryKey: ['backup-targets-for-tasks'],
    queryFn: () =>
      api.mcp<{ modules?: Array<{ name: string; label: string }> }>(
        'toolbox.backup_targets_list',
        {},
      ),
    enabled: defaultTool === 'backup_module' || toolFilter === 'backup',
    staleTime: 60_000,
  });

  // Fetch tasks with details
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['scheduled-tasks', toolFilter],
    queryFn: () => api.mcp<{ tasks?: ScheduledTask[] }>('tasks.task_list', { include_details: true }),
    staleTime: 30_000,
  });

  const rawTasks = (tasksData as { tasks?: unknown })?.tasks;
  const allTasks = (Array.isArray(rawTasks) ? rawTasks : []) as ScheduledTask[];
  const filteredTasks = allTasks.filter((t) => {
    const matchTool = String(t.tool ?? '').includes(toolFilter) ||
      String(t.name ?? '').includes(toolFilter);
    if (!matchTool) return false;
    if (filterEnabled === 'on' && !t.enabled) return false;
    if (filterEnabled === 'off' && t.enabled) return false;
    const q = taskSearch.trim().toLowerCase();
    if (q) {
      const hay = `${t.name} ${t.tool} ${JSON.stringify(t.args ?? {})}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Create task
  const createMut = useMutation({
    mutationFn: () => {
      const preset = FREQUENCY_PRESETS.find((p) => p.value === freqPreset);
      let cronExpr = (freqPreset === 'custom' ? customCron : (preset?.cron ?? '0 3 * * *')).trim();
      const atMap: Record<string, string> = {
        '@hourly': '0 * * * *',
        '@daily': '0 2 * * *',
        '@weekly': '0 3 * * 0',
        '@monthly': '0 3 1 * *',
      };
      if (atMap[cronExpr.toLowerCase()]) cronExpr = atMap[cronExpr.toLowerCase()];

      const triggerConfig = triggerType === 'cron'
        ? { cron: cronExpr }
        : triggerType === 'interval'
        ? { seconds: intervalSec }
        : { event: eventName };

      const name = taskName || `${toolFilter}-${triggerType}-${Date.now().toString(36)}`;

      if (!defaultTool) {
        toast.error(t('tasks.noToolSelected', 'Nebyl vybrán nástroj'));
        return Promise.reject(new Error('no tool'));
      }
      const args: Record<string, unknown> = { ...(defaultArgs ?? {}) };
      if (defaultTool === 'backup_module' || (toolFilter === 'backup' && defaultTool === 'backup_module')) {
        if (!argModule.trim()) {
          toast.error(t('tasks.selectBackupModule', 'Vyber modul k zálohování'));
          return Promise.reject(new Error('no module'));
        }
        args.module = argModule.trim();
      }
      if (Object.values(args).some((v) => v === '' || v === undefined)) {
        toast.error(t('tasks.fillAllFields', 'Vyplňte všechna pole'));
        return Promise.reject(new Error('empty args'));
      }
      return api.mcp('tasks.task_create', {
            name,
            module: defaultModule,
            tool: defaultTool,
            args,
            trigger_type: triggerType,
            trigger_config: triggerConfig,
            enabled: true,
          });
    },
    onSuccess: () => {
      toast.success(t('tasks.taskCreatedToast', 'Úloha vytvořena'));
      setShowCreate(false);
      setTaskName('');
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', toolFilter] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Toggle task
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.mcp('tasks.task_update', { id, enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-tasks', toolFilter] }),
  });

  // Delete task
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('tasks.task_delete', { id }),
    onSuccess: () => {
      toast.success(t('tasks.taskDeletedToast', 'Úloha smazána'));
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', toolFilter] });
    },
  });

  // Run now
  const runMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp('tasks.task_run', { id }),
    onSuccess: () => toast.success(t('tasks.startedToast', 'Spuštěno')),
  });

  // Update task
  const updateMut = useMutation({
    mutationFn: (params: {
      id: string;
      name?: string;
      trigger_type?: string;
      trigger_config?: Record<string, unknown>;
      args?: Record<string, unknown>;
    }) =>
      api.mcp('tasks.task_update', params),
    onSuccess: () => {
      toast.success(t('tasks.taskUpdatedToast', 'Úloha upravena'));
      setEditingTask(null);
      qc.invalidateQueries({ queryKey: ['scheduled-tasks', toolFilter] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Fetch task history
  const { data: historyData, isLoading: loadingHistory } = useQuery({
    queryKey: ['task-history', viewingTask?.id],
    queryFn: () =>
      api.mcp<{ runs?: TaskRun[] }>('tasks.task_history', {
        id: viewingTask!.id,
        limit: 10,
      }),
    enabled: !!viewingTask,
    staleTime: 10_000,
  });

  const runs = ((historyData as { runs?: TaskRun[] })?.runs ?? []) as TaskRun[];

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-2 w-full'>
          <CardTitle icon={CalendarClock}>
            {t('tasks.pageTitle', 'Task Manager')} ({filteredTasks.length}
            {filteredTasks.length !== allTasks.filter((t) =>
              String(t.tool ?? '').includes(toolFilter) || String(t.name ?? '').includes(toolFilter)
            ).length
              ? ` / ${allTasks.filter((t) =>
                String(t.tool ?? '').includes(toolFilter) || String(t.name ?? '').includes(toolFilter)
              ).length}`
              : ''}
            )
          </CardTitle>
          {!readonly && (
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setShowCreate(!showCreate)}
              icon={showCreate ? <X size={12} /> : <Plus size={12} />}
            >
              {showCreate ? t('ui.cancel', 'Zrušit') : t('ui.create', 'Vytvořit')}
            </Button>
          )}
        </div>
      </CardHeader>

      {/* Edit modal */}
      <Modal
        open={!!editingTask}
        onClose={() => setEditingTask(null)}
        title={t('tasks.editTaskNamed', 'Upravit úlohu: {{name}}', { name: editingTask?.name ?? '' })}
        size='md'
      >
        {editingTask && (
          <EditTaskForm
            task={editingTask}
            onSubmit={(data) => updateMut.mutate({ id: editingTask.id, ...data })}
            isPending={updateMut.isPending}
            onCancel={() => setEditingTask(null)}
          />
        )}
      </Modal>

      {/* History modal */}
      <Modal
        open={!!viewingTask}
        onClose={() => setViewingTask(null)}
        title={t('tasks.runHistoryNamed', 'Historie běhů: {{name}}', { name: viewingTask?.name ?? '' })}
        size='xl'
      >
        <div className='space-y-3'>
          {loadingHistory
            ? (
              <div className='flex justify-center py-4'>
                <Loader2 size={14} className='animate-spin text-gray-600' />
              </div>
            )
            : runs.length === 0
            ? <p className='text-xs text-gray-600 py-2'>{t('tasks.noRunsYet', 'Zatím žádné běhy')}</p>
            : (
              <div className='space-y-2 max-h-96 overflow-y-auto'>
                {runs.map((run) => (
                  <div key={run.id} className='border border-gray-800 rounded-lg p-3 space-y-1'>
                    <div className='flex items-center justify-between text-xs'>
                      <div className='flex items-center gap-2'>
                        <Badge
                          variant={run.status === 'ok'
                            ? 'success'
                            : run.status === 'running'
                            ? 'info'
                            : 'error'}
                        >
                          {run.status}
                        </Badge>
                        <span className='text-gray-500'>
                          {new Date(run.started_at).toLocaleString(i18n.language)}
                        </span>
                      </div>
                      <span className='text-gray-600'>
                        {run.triggered_by === 'cron' || run.triggered_by === 'interval'
                          ? t('tasks.automatically', 'automaticky')
                          : run.triggered_by}
                      </span>
                    </div>
                    {run.output && (
                      <pre className='text-[10px] text-gray-400 bg-gray-900/50 p-2 rounded overflow-x-auto max-h-24'>
                        {run.output}
                      </pre>
                    )}
                    {run.error && (
                      <pre className='text-[10px] text-red-400 bg-red-950/30 p-2 rounded overflow-x-auto max-h-24'>
                        {run.error}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
      </Modal>
      <CardContent className='space-y-3'>
        {/* Filters */}
        <div className='flex flex-col sm:flex-row gap-2'>
          <Input
            size='sm'
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            placeholder={t('tasks.searchTaskPlaceholder', 'Hledat úlohu, tool, args…')}
            className='flex-1'
          />
          <Select
            size='sm'
            className='sm:w-32'
            value={filterEnabled}
            onChange={(e) => setFilterEnabled(e.target.value as 'all' | 'on' | 'off')}
            options={[
              { value: 'all', label: t('tasks.filterAll', 'Vše') },
              { value: 'on', label: t('tasks.statActive', 'Aktivní') },
              { value: 'off', label: t('tasks.filterOff', 'Vypnuté') },
            ]}
          />
        </div>

        {/* Create form */}
        {showCreate && (
          <div className='mb-2 pb-4 border border-gray-800 rounded-xl p-3 space-y-3 bg-gray-950/40'>
            <Input
              size='sm'
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder={t('tasks.optionalName', 'Název úlohy (volitelné)')}
            />

            {(defaultTool === 'backup_module' || toolFilter === 'backup') && (
              <div className='space-y-1'>
                <div className='text-[10px] text-gray-500 uppercase tracking-wide'>
                  {t('tasks.backupModuleLabel', 'Modul k zálohování')}
                </div>
                <Select
                  size='sm'
                  value={argModule}
                  onChange={(e) => setArgModule(e.target.value)}
                  options={[
                    { value: '', label: t('backup.selectModule', '— vyber modul —') },
                    ...(Array.isArray(modulesQ.data?.modules) ? modulesQ.data.modules : []).map(
                      (m) => ({
                        value: m.name,
                        label: m.label !== m.name ? `${m.label} (${m.name})` : m.name,
                      }),
                    ),
                  ]}
                />
              </div>
            )}

            <div className='flex gap-1.5 flex-wrap'>
              {(['cron', 'interval', 'event'] as const).map((tt) => (
                <button
                  key={tt}
                  type='button'
                  onClick={() => setTriggerType(tt)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    triggerType === tt
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
                >
                  {tt === 'cron'
                    ? t('tasks.triggerRegular', 'Pravidelně')
                    : tt === 'interval'
                    ? t('tasks.triggerIntervalLabel', 'Interval')
                    : t('tasks.triggerOnEvent', 'Na událost')}
                </button>
              ))}
            </div>

            {triggerType === 'cron' && (
              <div className='space-y-2'>
                <Select
                  size='sm'
                  value={freqPreset}
                  onChange={(e) => setFreqPreset(e.target.value)}
                  options={FREQUENCY_PRESETS.map((p) => ({
                    value: p.value,
                    label: t(`tasks.freq.${p.value}`, p.label),
                  }))}
                />
                {freqPreset === 'custom' && (
                  <Input
                    size='sm'
                    value={customCron}
                    onChange={(e) => setCustomCron(e.target.value)}
                    placeholder='*/5 * * * *  nebo  @daily'
                    className='font-mono'
                  />
                )}
              </div>
            )}

            {triggerType === 'interval' && (
              <div className='flex items-center gap-2'>
                <span className='text-xs text-gray-500'>{t('tasks.every', 'Každých')}</span>
                <Input
                  size='sm'
                  type='number'
                  value={intervalSec}
                  onChange={(e) => setIntervalSec(Number(e.target.value))}
                  min={30}
                  max={86400}
                  className='w-24'
                />
                <span className='text-xs text-gray-500'>{t('tasks.seconds', 'sekund')}</span>
              </div>
            )}

            {triggerType === 'event' && (
              <Select
                size='sm'
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                options={EVENT_PRESETS.map((e) => ({ value: e, label: e }))}
                placeholder={t('tasks.selectEvent', 'Vyber událost...')}
              />
            )}

            <Button
              variant='primary'
              size='sm'
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending || (triggerType === 'event' && !eventName)}
              icon={createMut.isPending
                ? <Loader2 size={12} className='animate-spin' />
                : <Plus size={12} />}
            >
              {t('tasks.createButton', 'Vytvořit')}
            </Button>
          </div>
        )}

        {/* Task list */}
        {isLoading
          ? (
            <div className='flex justify-center py-4'>
              <Loader2 size={14} className='animate-spin text-gray-600' />
            </div>
          )
          : filteredTasks.length === 0
          ? (
            <p className='text-xs text-gray-600 py-2'>
              {t('ui.noScheduledTasks', 'Žádné plánované úlohy (uprav filtr nebo přidej Nová).')}
            </p>
          )
          : (
            <div className='divide-y divide-gray-800 max-h-[28rem] overflow-y-auto'>
              {filteredTasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onToggle={(enabled) => toggleMut.mutate({ id: task.id, enabled })}
                  onDelete={() => setDeleteTask(task)}
                  onRun={() => runMut.mutate(task.id)}
                  onEdit={() => setEditingTask(task)}
                  onViewHistory={() => setViewingTask(task)}
                />
              ))}
            </div>
          )}
      </CardContent>

      <ConfirmDialog
        open={!!deleteTask}
        title={t('ui.deleteItemTitle', 'Smazat položku?')}
        description={t('ui.deleteTaskConfirm', 'Smazat „{{name}}“?', { name: deleteTask?.name ?? '' })}
        confirmLabel={t('ui.delete', 'Smazat')}
        cancelLabel={t('ui.cancel', 'Zrušit')}
        danger
        onConfirm={() => {
          if (deleteTask) deleteMut.mutate(deleteTask.id);
          setDeleteTask(null);
        }}
        onCancel={() => setDeleteTask(null)}
      />
    </Card>
  );
}

function TaskRow({ task, onToggle, onDelete, onRun, onEdit, onViewHistory }: {
  task: ScheduledTask;
  onToggle: (enabled: boolean) => void;
  onDelete: () => void;
  onRun: () => void;
  onEdit: () => void;
  onViewHistory: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const trigger = task.trigger_type === 'cron'
    ? (task.trigger_config as { cron?: string })?.cron ?? '?'
    : task.trigger_type === 'interval'
    ? t('tasks.everyNSeconds', 'každých {{seconds}}s', {
      seconds: (task.trigger_config as { seconds?: number })?.seconds ?? '?',
    })
    : `event: ${(task.trigger_config as { event?: string })?.event ?? '?'}`;

  return (
    <div className='py-2'>
      <div className='flex items-center gap-2'>
        <Toggle value={task.enabled} onSave={(v) => onToggle(v)} />
        <button onClick={() => setExpanded(!expanded)} className='flex-1 text-left min-w-0'>
          <span className='text-sm text-white font-medium'>{task.name}</span>
          <span className='text-[10px] text-gray-600 font-mono ml-2'>{trigger}</span>
        </button>
        {task.last_run_status && (
          <Badge
            variant={task.last_run_status === 'ok'
              ? 'success'
              : task.last_run_status === 'running'
              ? 'info'
              : 'error'}
          >
            {task.last_run_status}
          </Badge>
        )}
        <button
          onClick={onViewHistory}
          className='p-1 text-gray-600 hover:text-blue-400 rounded'
          title={t('tasks.runHistory', 'Historie běhů')}
        >
          <History size={12} />
        </button>
        <button
          onClick={onRun}
          className='p-1 text-gray-600 hover:text-emerald-400 rounded'
          title={t('backup.runNow', 'Spustit teď')}
        >
          <Play size={12} />
        </button>
        <button
          onClick={onEdit}
          className='p-1 text-gray-600 hover:text-yellow-400 rounded'
          title={t('common.edit', 'Upravit')}
        >
          <Edit2 size={12} />
        </button>
        <button
          onClick={onDelete}
          className='p-1 text-gray-600 hover:text-red-400 rounded'
          title={t('common.delete', 'Smazat')}
        >
          <Trash2 size={12} />
        </button>
        {expanded
          ? <ChevronDown size={12} className='text-gray-600' />
          : <ChevronRight size={12} className='text-gray-600' />}
      </div>
      {expanded && (
        <div className='ml-8 mt-1 text-[10px] text-gray-600 space-y-0.5'>
          <div>
            Tool: <span className='text-gray-400 font-mono'>{task.tool === task.module || task.tool.startsWith(`${task.module}.`) ? task.tool : `${task.module}.${task.tool}`}</span>
          </div>
          {task.args && Object.keys(task.args).length > 0 && (
            <div>
              Args: <span className='text-gray-400 font-mono'>{JSON.stringify(task.args)}</span>
            </div>
          )}
          {task.last_run_at && (
            <div>
              {t('tasks.lastRunAt', 'Poslední běh: {{date}}', {
                date: new Date(task.last_run_at).toLocaleString(i18n.language),
              })}
            </div>
          )}
          {task.next_run_at && (
            <div>
              {t('tasks.nextRunAt', 'Příští běh: {{date}}', {
                date: new Date(task.next_run_at).toLocaleString(i18n.language),
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Edit Task Form ────────────────────────────────────────────────────────────

function EditTaskForm({ task, onSubmit, isPending, onCancel }: {
  task: ScheduledTask;
  onSubmit: (data: { name?: string; trigger_type?: string; trigger_config?: Record<string, unknown>; args?: Record<string, unknown> }) => void;
  isPending: boolean;
  onCancel: () => void;
}) {
  const [name, setName] = useState(task.name);
  const [triggerType, setTriggerType] = useState(task.trigger_type);
  const [freqPreset, setFreqPreset] = useState('custom');
  const [customCron, setCustomCron] = useState((task.trigger_config as { cron?: string })?.cron ?? '');
  const [intervalSec, setIntervalSec] = useState((task.trigger_config as { seconds?: number })?.seconds ?? 300);
  const [eventName, setEventName] = useState((task.trigger_config as { event?: string })?.event ?? '');
  const [argsText, setArgsText] = useState(task.args ? JSON.stringify(task.args, null, 2) : '{}');
  const { t } = useTranslation();

  const handleSubmit = () => {
    try {
      const args = JSON.parse(argsText);
      const preset = FREQUENCY_PRESETS.find((p) => p.value === freqPreset);
      const cronExpr = freqPreset === 'custom' ? customCron : (preset?.cron ?? '0 3 * * *');

      const triggerConfig = triggerType === 'cron'
        ? { cron: cronExpr }
        : triggerType === 'interval'
        ? { seconds: intervalSec }
        : { event: eventName };

      onSubmit({ name, trigger_type: triggerType, trigger_config: triggerConfig, args });
    } catch (e) {
      toast.error(t('tasks.invalidJsonArgs', 'Neplatný JSON v args'));
    }
  };

  return (
    <div className='space-y-3'>
      <Input
        size='sm'
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('tasks.taskNamePlaceholder', 'Název úlohy *')}
      />

      <div className='flex gap-1.5'>
        {(['cron', 'interval', 'event'] as const).map((tt) => (
          <button
            key={tt}
            onClick={() => setTriggerType(tt)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              triggerType === tt
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {tt === 'cron'
              ? t('tasks.triggerRegular', 'Pravidelně')
              : tt === 'interval'
              ? t('tasks.triggerIntervalLabel', 'Interval')
              : t('tasks.triggerOnEvent', 'Na událost')}
          </button>
        ))}
      </div>

      {triggerType === 'cron' && (
        <div className='space-y-2'>
          <Select
            size='sm'
            value={freqPreset}
            onChange={(e) => setFreqPreset(e.target.value)}
            options={FREQUENCY_PRESETS.map((p) => ({
              value: p.value,
              label: t(`tasks.freq.${p.value}`, p.label),
            }))}
          />
          {freqPreset === 'custom' && (
            <Input
              size='sm'
              value={customCron}
              onChange={(e) => setCustomCron(e.target.value)}
              placeholder='*/5 * * * *'
              className='font-mono'
            />
          )}
        </div>
      )}

      {triggerType === 'interval' && (
        <div className='flex items-center gap-2'>
          <span className='text-xs text-gray-500'>{t('tasks.every', 'Každých')}</span>
          <Input
            size='sm'
            type='number'
            value={intervalSec}
            onChange={(e) => setIntervalSec(Number(e.target.value))}
            min={30}
            max={86400}
            className='w-24'
          />
          <span className='text-xs text-gray-500'>{t('tasks.seconds', 'sekund')}</span>
        </div>
      )}

      {triggerType === 'event' && (
        <Select
          size='sm'
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          options={EVENT_PRESETS.map((e) => ({ value: e, label: e }))}
          placeholder={t('tasks.selectEvent', 'Vyber událost...')}
        />
      )}

      <div>
        <label className='text-xs text-gray-500'>Args (JSON)</label>
        <textarea
          value={argsText}
          onChange={(e) => setArgsText(e.target.value)}
          className='w-full h-24 px-3 text-xs rounded-xl border bg-gray-900/40 text-white focus:outline-none focus:border-blue-500 font-mono'
        />
      </div>

      <div className='flex gap-2 pt-2 border-t border-gray-800'>
        <Button variant='secondary' size='sm' onClick={onCancel}>
          {t('common.cancel', 'Zrušit')}
        </Button>
        <Button
          variant='primary'
          size='sm'
          onClick={handleSubmit}
          disabled={isPending}
          icon={isPending ? <Loader2 size={12} className='animate-spin' /> : <Edit2 size={12} />}
        >
          {t('common.save', 'Uložit')}
        </Button>
      </div>
    </div>
  );
}
